import moment from 'moment';

/**
 * A collection of helper functions and types for Javascript's built-in Date model.
 */
export namespace DateHelpers {

  /**
   * Indexes representing the days of the week for Javascript's built-in Date model.
   */
  export enum DayOfWeek {
    SUNDAY = 0,
    MONDAY = 1,
    TUESDAY = 2,
    WEDNESDAY = 3,
    THURSDAY = 4,
    FRIDAY = 5,
    SATURDAY = 6,
  }

  export enum Month {
    January = 0,
    February = 1,
    March = 2,
    April = 3,
    May = 4,
    June = 5,
    July = 6,
    August = 7,
    September = 8,
    October = 9,
    November = 10,
    December = 11,
  }

  /**
   * Returns the current date, exactly the same as calling `new Date()` directly.
   * This is useful because it allows for easy spying/mocking during unit tests,
   * where you don't want the actual clock time to influence the test.
   */
  export function now(): Date {
    return new Date();
  }

  /**
   * Formats the given date in ISO 8601 format (YYYY-MM-DD).
   */
  export function toISO8601(date: Date): string {
    // grabs the first 10 characters from full ISO string output.
    return date.toISOString().slice(0, 10);
  }

  /**
   * See {@link now} and {@link toISO8601}.
   */
  export function nowAsISO8601(): string {
    return toISO8601(now());
  }

  export function parseISO8601(iso8601string: string): Date {
    return moment(iso8601string, 'YYYY-MM-DD').utc().toDate();
  }

  export function toDisplayDateTime(iso8601string: string): string {
    return moment(iso8601string).format('M/D/YY [@]h:mm A');
  }

  /**
   * Get a specific day of the week for the week containing the given date.
   * If no date is passed in, the current date is used.
   *
   * **Weeks start on Sunday and end on Saturday.**
   */
  export function getDayOfWeekAroundDate(dayOfWeek: DayOfWeek, givenDate?: Date): Date {
    const date: Date = (givenDate) ? new Date(givenDate) : now();
    const dayOfMonth = date.getDate() - date.getDay() + dayOfWeek;
    date.setDate(dayOfMonth);
    return date;
  }

  /**
   * String version of getDayOfWeekAroundDate.
   */
  export function getStringDayOfWeekAroundDate(dayOfWeek: DayOfWeek, givenDate?: string): string {
    return this.toISO8601(this.getDayOfWeekAroundDate(dayOfWeek, this.parseISO8601(givenDate)));
  }

  /**
   * Get the start of the week for the given date, which is always a Sunday.
   * If no date is passed in, the current date is used.
   */
  export function getStartOfWeek(givenDate?: Date): Date {
    return getDayOfWeekAroundDate(DayOfWeek.SUNDAY, givenDate);
  }


  /**
   * Adjusts the given date by a specified number of weeks.
   * @param date The date to adjust.
   * @param weeks The number of weeks to adjust the date. Positive for future dates, negative for past dates.
   * @returns The adjusted date.
   */
  export function adjustDateByWeeks(date: Date, weeks: number): Date {
    const adjustedDate = new Date(date);
    adjustedDate.setDate(date.getDate() + (weeks * 7));
    return adjustedDate;
  }

  /**
   * Adjusts the given date by one week forward.
   * @param date The date to adjust.
   * @returns The adjusted date, one week later.
   */
  export function addOneWeek(date: Date): Date {
    return adjustDateByWeeks(date, 1);
  }

  /**
   * Adjusts the given date by one week backward.
   * @param date The date to adjust.
   * @returns The adjusted date, one week earlier.
   */
  export function subtractOneWeek(date: Date): Date {
    return adjustDateByWeeks(date, -1);
  }

   /**
   * Adjusts the given date by one day backward.
   * @param date The date to adjust.
   * @returns The adjusted date, one day earlier.
   */
  export function subtractOneDay(date: Date): Date {
    const adjustedDate = new Date(date);
    adjustedDate.setDate(date.getDate() - 1);
    return adjustedDate;
  }


  /**
   * Get the end of the week for the given date, which is always a Saturday.
   * If no date is passed in, the current date is used.
   */
  export function getEndOfWeek(givenDate?: Date): Date {
    return getDayOfWeekAroundDate(DayOfWeek.SATURDAY, givenDate);
  }


  export function compareDates(first: Date, second: Date) {
    return first?.toISOString() === second?.toISOString();
  }

  export function isAfter(first: Date, second: Date): boolean | undefined {
    if (!first || !second) {
      return undefined;
    }
    return moment(first).isAfter(second);
  }

  export function isBefore(first: Date, second: Date): boolean | undefined {
    if (!first || !second) {
      return undefined;
    }
    return moment(first).isBefore(second);
  }

  /**
   * Formats a date as month, day and year separated by slashes.
   */
  export function formatShortDate(date: Date): string {
    return moment(date).format('M/D/YY');
  }
}
